<template>
  <v-autocomplete
    v-if="remote"
    ref="fieldRef"
    v-tooltip:top="getTooltipDisplayValue(remote.valueRef)"
    :menu-props="{ maxWidth: '100%' }"
    :model-value="remote.valueRef"
    :items="remote.itemsRef"
    :search="remote.searchRef"
    item-title="title"
    item-value="value"
    :loading="remote.loadingRef"
    :rules="rules"
    :label="props.label"
    v-bind="componentBind"
    @update:search="remote.searchUpdate"
    @blur="onBlur"
    @update:model-value="onUpdateModelValue">
    <template v-if="itemTemplate" #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" lines="two">
        <template #title>{{ itemTemplate(item).title }}</template>
        <template #subtitle>
          <span class="text-caption">{{ itemTemplate(item).subtitle }}</span>
        </template>
      </v-list-item>
    </template>
    <template v-for="name in forwardedSlotNames" :key="name" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>
  </v-autocomplete>
</template>

<script setup>
import { computed, onMounted, ref, useSlots, watch } from "vue";
import { useRemoteAutocompleteSearch } from "@/utils/autocomplete";
import { useFieldTooltip } from "@/composables/useFieldTooltip";

const { getTooltipDisplayValue } = useFieldTooltip();

const emit = defineEmits(["update:modelValue"]);
const props = defineProps({
  remoteSearch: {
    type: Function,
    required: true,
  },
  modelValue: {
    type: [String, null],
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  rules: {
    type: Array,
    required: false,
    default: () => [],
  },
  allowFreeText: {
    type: Boolean,
    required: false,
    default: true,
  },
  allowEmptySearch: {
    type: Boolean,
    required: false,
    default: false,
  },
  componentBind: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  itemTemplate: {
    type: Function,
    required: false,
    default: null,
  },
});
const fieldRef = ref(null);
const slots = useSlots();
// Pass caller slots (#item, #no-data, ...) through to v-autocomplete; itemTemplate keeps precedence for #item
const forwardedSlotNames = computed(() =>
  Object.keys(slots).filter((name) => !(name === "item" && props.itemTemplate))
);
const remote = ref(
  useRemoteAutocompleteSearch(props.remoteSearch, {
    allowFreeText: props.allowFreeText,
    allowEmptySearch: props.allowEmptySearch,
  })
);

defineExpose({
  fieldRef,
  remote,
});
// We send in all autocomplete values to the rules
const rules = ref(props.rules.map((rule) => (v) => rule(v, { ...remote.value })));

onMounted(() => {
  remote.value.valueRef = props.modelValue ?? "";
  remote.value.searchUpdate(props.modelValue ?? "");
});

const onBlur = () => {
  // If free text is allowed we should update on tab-out
  // Emit valueRef (the actual value) not the visible text (which may be the title)
  if (props.allowFreeText) {
    emit("update:modelValue", remote.value.valueRef);
  }
};

const onUpdateModelValue = (value) => {
  remote.value.valueRef = value || "";
  emit("update:modelValue", value);
};

watch(
  () => remote.value?.itemsRef,
  () => {
    fieldRef.value?.validate();
  }
);

watch(
  () => props.modelValue,
  () => {
    remote.value.valueRef = props.modelValue;
    // Don't trigger a search or update the search text if the value is a number (internal ID)
    // as it would cause the ID to appear in the input box instead of the display name/NPI.
    if (typeof props.modelValue === "string") {
      remote.value.searchUpdate(props.modelValue);
    }
  }
);
</script>
